import api from './client'
import { unwrap, toNum } from './global'

// ---------------------------------------------------------------------------
// AFD 自有协议 RPC 包装 (wire 格式) (SA-RPC-COVER)
//
//   /api/shutdown       POST  → afd.shutdown       (webAuth required)
//   /api/save-session   POST  → afd.saveSession    (webAuth required)
//   /api/bt-stats       GET   → system.getBTStats  (read-only)
//
//   /api/tasks/{gid}/uri          POST → afd.changeUri          (webAuth)
//   /api/tasks/{gid}/remove-result POST → afd.removeDownloadResult (webAuth)
//   /api/multicall                POST → system.multicall        (no auth)
//
// All handlers live in `internal/api/tasks_webui.go`; this file is the
// strongly-typed front-end surface so callers don't reach into `client.ts`
// directly. The legacy `purgeDownloadResult` RPC is intentionally kept in
// `tasks.ts` because the UI wires it next to the stopped-results list.
// ---------------------------------------------------------------------------

/** shutdown requires the optional `confirm` flag in the request body. */
export interface ShutdownPayload {
  /** AFD wire 协议 confirm token — when omitted the backend still
   *  forwards a request (with no params) for parity with the legacy REST path. */
  confirm?: string
}

export async function shutdown(payload?: ShutdownPayload): Promise<void> {
  await api.post<void>('/api/shutdown', payload ?? {})
}

/** Manually persist the current AFD session (active/waiting). */
export async function saveSession(): Promise<void> {
  await api.post<void>('/api/save-session', {})
}

/**
 * Remove a single completed/error/removed task result. The backend route is
 * exposed as `POST /api/tasks/{gid}/remove-result` (webAuth required). Idempotent
 * — returns 200 even if the gid isn't present (matches AFD wire 协议).
 */
export async function removeDownloadResult(gid: string): Promise<string> {
  // AFD wire 协议: AFD.removeDownloadResult(gid) returns the gid.
  return api.post<string>(`/api/tasks/${encodeURIComponent(gid)}/remove-result`, {})
}

// ---------------------------------------------------------------------------
// afd.changeUri — replace URIs at a given file index for an existing task.
//
// AFD wire 协议: AFD.changeUri(gid, fileIndex, delUris, addUris)
//   fileIndex — 0-based file index inside the task (0 = whole-task / single-file)
//   delUris   — URIs to remove (string[]; empty = remove none)
//   addUris   — URIs to add    (string[]; empty = add none)
//
// Returns `{removedCount, addedCount}` (already wrapped in okJSON envelope).
// ---------------------------------------------------------------------------

export interface ChangeUriPayload {
  /** 0-based file index (0 for whole-task). Default 0. */
  fileIndex?: number
  /** URIs to remove. Empty array = don't remove any. */
  delUris?: string[]
  /** URIs to add. Empty array = don't add any. */
  addUris?: string[]
}

export interface ChangeUriResult {
  removedCount: number
  addedCount: number
}

export async function changeUri(gid: string, payload: ChangeUriPayload = {}): Promise<ChangeUriResult> {
  const res = await api.post<unknown>(
    `/api/tasks/${encodeURIComponent(gid)}/uri`,
    {
      fileIndex: payload.fileIndex ?? 0,
      delUris: payload.delUris ?? [],
      addUris: payload.addUris ?? [],
    },
  )
  return normalizeChangeUri(unwrap<ChangeUriResult | ChangeUriResult[]>(res))
}

/** Defensive normalizer — backend always returns a single object, but old
 *  builds (pre SA-ARIANG) returned the raw map inline, so accept either. */
export function normalizeChangeUri(
  raw: Partial<ChangeUriResult> | Partial<ChangeUriResult>[] | null | undefined,
): ChangeUriResult {
  const r = Array.isArray(raw) ? raw[0] : raw
  return {
    removedCount: toNum(r?.removedCount),
    addedCount: toNum(r?.addedCount),
  }
}

// ---------------------------------------------------------------------------
// system.multicall — batch multiple RPCs into one HTTP roundtrip.
//
// Wire-protocol: system.multicall(methods) where methods = [[name, params], ...]
// Front-end shape (also supported by /api/multicall):
//   1) {method, params}            — single call shortcut
//   2) {methods: [{method, params}, ...]} — batch
//
// Result is `MulticallResult[]`, one per call, in request order. Each entry
// is either `{result: T}` or `{faultCode, faultString}`.
// ---------------------------------------------------------------------------

export interface MulticallSingleCall {
  method: string
  params?: unknown[]
}

export type MulticallBody = MulticallSingleCall | { methods: MulticallSingleCall[] }

export type MulticallResult =
  | { result: unknown; faultCode?: never; faultString?: never }
  | { result?: never; faultCode: number; faultString: string }
  | Record<string, unknown>

/**
 * Run a batch of AFD RPC calls in one HTTP roundtrip. Either a single call
 * (pass a plain object) or an array of calls. Returns the raw per-call results
 * in order — caller is responsible for unwrapping `.result` / handling faults.
 */
export async function multicall(input: MulticallSingleCall | MulticallSingleCall[]): Promise<MulticallResult[]> {
  const body: MulticallBody = Array.isArray(input)
    ? { methods: input }
    : input
  const res = await api.post<unknown>('/api/multicall', body)
  return normalizeMulticall(unwrap<MulticallResult[] | MulticallResult>(res))
}

/**
 * Normalize the multicall response. The wire-format result is `[]` of
 * `{result: ...}` / `{faultCode, faultString}` maps. Some legacy wrappers
 * returned the bare value (no array) when only one method was requested —
 * we defensively unwrap both.
 */
export function normalizeMulticall(raw: unknown): MulticallResult[] {
  if (!Array.isArray(raw)) {
    return raw ? [raw as MulticallResult] : []
  }
  return raw as MulticallResult[]
}

/** Convenience helper — extract `{result}` payload or throw on `faultCode`. */
export function unwrapMulticall<T = unknown>(entry: MulticallResult): T {
  if ('faultCode' in entry && typeof entry.faultCode === 'number') {
    const msg = (entry as { faultString?: string }).faultString ?? `multicall fault ${entry.faultCode}`
    const err = new Error(msg) as Error & { status: number; faultCode: number }
    err.name = 'MulticallFault'
    err.status = entry.faultCode
    err.faultCode = entry.faultCode
    throw err
  }
  return (entry as { result: T }).result
}

// ---------------------------------------------------------------------------
// system.getBTStats — cumulative BitTorrent upload/download bytes
//
// Backend (`internal/api/handlers_torrent.go: systemGetBTStats`) returns every
// numeric field as a **decimal string**, wrapped by `okJSON` into `{ok, data}`.
// The DTO mirrors the raw payload; `normalizeBTStats` parses the strings into
// numbers for the UI.
// ---------------------------------------------------------------------------

export interface RawBTStats {
  totalUploaded: string
  totalDownloaded: string
  updatedAt: string
}

export interface BTStats {
  /** bytes */
  totalUploaded: number
  /** bytes */
  totalDownloaded: number
  /** unix seconds */
  updatedAt: number
}

export function normalizeBTStats(raw: Partial<RawBTStats> | null | undefined): BTStats {
  return {
    totalUploaded: toNum(raw?.totalUploaded),
    totalDownloaded: toNum(raw?.totalDownloaded),
    updatedAt: toNum(raw?.updatedAt),
  }
}

export async function getBTStats(): Promise<BTStats> {
  const res = await api.get<unknown>('/api/bt-stats')
  return normalizeBTStats(unwrap<RawBTStats>(res))
}